'use client'

import { faArrowCircleLeft, faArrowCircleRight, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ChangeEvent, memo, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import SimpleReactValidator from "simple-react-validator";
import Swal from "sweetalert2";
import * as authenticateAction from "../../redux/actions/authenticate";
import * as commonAction from "../../redux/actions/common";
import { makeNavigationSiteAnalytics } from "@/services/simpleApis";

const eye = <FontAwesomeIcon icon={faEye} />;
const eyeSlash = <FontAwesomeIcon icon={faEyeSlash} />;

type formDataType = {
  first_name: string,
  last_name: string,
  username: string,
  email: string,
  phone: string,
  country_id: string,
  password: string,
  ConfirmPassword: string,
  agreed: boolean
}

type registerFormDataType = {
  first_name: string;
  last_name: string;
  password: string;
  username: string;
  email: string;
  phone: string;
  country_code: string;
  ConfirmPassword: undefined;
}

type userExists = {
  data: { userExists: boolean }
}
interface RegisterFormProps {
  countryData: Record<string, any> | undefined, currencyData: Record<string, any> | undefined, industryData: Record<string, any> | undefined, regionData: Record<string, any> | undefined, isRegisterLoader: boolean,
  getCountry(): Promise<void>, getCurrency(): Promise<void>, getRegion(): Promise<void>, getIndustry(): Promise<void>, getMaturityYears(): Promise<void>, getIssueType(): Promise<void>, registerUser(formData: registerFormDataType): Promise<void>, resendActivationEmail(data: { email: string }): Promise<void>, checkIsUserNameOrEmailAvailable(data: { params: { value: string, isEmail: boolean } }): Promise<userExists>
}
const RegisterForm = ({
  countryData, currencyData, industryData, regionData,
  getCountry, getCurrency, getRegion, getIndustry, getMaturityYears, getIssueType, registerUser, resendActivationEmail, checkIsUserNameOrEmailAvailable
}: RegisterFormProps) => {
  const init = {
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    phone: "",
    country_id: "US",
    password: "",
    ConfirmPassword: "",
    agreed: false
  };

  const [formData, setFormData] = useState<formDataType>(init);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((pre) => ({
      ...pre,
      [name]: type === 'checkbox' ? e.target.checked : value,
    }));
  }, [])

  const validator = useRef(new SimpleReactValidator({
    className: "text-red text-sm",
  }));

  const [loading, setLoading] = useState(false);

  const [, forceUpdate] = useState(0);

  const [passwordShown, setPasswordShown] = useState(false);
  const [confirmpasswordShown, setConfirmPasswordShown] = useState(false);
  const [isFormSubmit, setIsFormSubmit] = useState(false);

  const togglePasswordVisiblity = () => {
    setPasswordShown(passwordShown ? false : true);
  };

  const toggleConfirmPasswordVisiblity = () => {
    setConfirmPasswordShown(confirmpasswordShown ? false : true);
  };

  const [isAvailable, setIsAvailable] = useState({
    username: true,
    email: true,
  });

  const [isLoadingDropValues, setIsLoadingDropValues] = useState(false);

  const handleGetAllCommonData = async () => {
    const isCommonDataAvailable = !!countryData?.code && !!currencyData?.code && !!industryData?.code && !!regionData?.code
    if (isLoadingDropValues || isCommonDataAvailable) {
      return;
    } else {
      setIsLoadingDropValues(true);
      await Promise.all([getCountry(), getCurrency(), getRegion(), getIndustry(), getMaturityYears(), getIssueType()])
    }
  }

  const newStrongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#-])[A-Za-z\d@$!%*?&#-]{8,}$/;
  const password_check = useCallback((pass: string) => {
    return !(newStrongRegex.exec(pass) == null);
  }, [newStrongRegex])

  const router = useRouter()
  const handlesubmit = async () => {
    setLoading(true);
    try {
      const reqObj = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        username: formData.username.trim(),
        email: formData.email,
        phone: formData.phone,
        country_code: formData.country_id,
        ConfirmPassword: undefined
      };
      const isValid = validator.current.allValid();
      setIsFormSubmit(true);

      if (
        isValid &&
        password_check(formData.password) &&
        isAvailable.username &&
        isAvailable.email
      ) {
        try {
          registerUser(reqObj).then((data: any) => {
            if (data.code === 409) {
              // handle resend confirmation
              Swal.fire({
                text: "Your email is not verified yet. Do you want to resend confirmation email?",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, resend it!",
              }).then(async (result) => {
                if (result.isConfirmed) {
                  resendActivationEmail({ email: data.email });
                  router.push("/login");
                }
              });
            } else if (data?.code === 200) {
              setLoading(false);
              router.push("/registration-success");
            } else {
              setLoading(false);
            }
          });
        } catch (error) {
          setLoading(false);
          console.error("Error fetching bond data:", error);
        }
      } else {
        setLoading(false);
        validator.current.showMessages();
        forceUpdate(1);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching bond data:", error);
    }
  };

  useEffect(() => {
    handleGetAllCommonData();
  }, [])

  const [timer, setTimer] = useState<NodeJS.Timeout>();
  useEffect(() => {
    if (formData.username) {
      clearTimeout(timer);
      const newTimer = setTimeout(() => {
        const body = {
          params: {
            value: formData.username,
            isEmail: false,
          },
        };
        checkIsUserNameOrEmailAvailable(body).then((data) => {
          setIsAvailable((prev) => ({
            ...prev,
            username: !data?.data?.userExists,
          }));
        });
      }, 600);
      setTimer(newTimer);
    }
  }, [formData.username]);

  useEffect(() => {
    if (formData.email) {
      clearTimeout(timer);
      const newTimer = setTimeout(() => {
        const body = {
          params: {
            value: formData.email,
            isEmail: true,
          },
        };
        // console.log('Calling checkIsUserNameOrEmailAvailable');

        checkIsUserNameOrEmailAvailable(body).then((data) => {
          // console.log('Inside checkIsUserNameOrEmailAvailable');
          setIsAvailable((prev) => ({
            ...prev,
            email: !data?.data?.userExists,
          }));
        });
      }, 600);
      setTimer(newTimer);
    }
  }, [formData.email]);


  return (
    <form autoComplete="off">
      <div className="row">
        <div className="col-md-6 mb-4 align-self-center ">
          <div className="form-outline">
            <input
              type="text"
              id="firstName"
              autoComplete="off"
              placeholder="First Name"
              name="first_name"
              value={formData.first_name}
              className="form-control form-control-lg text-sm"
              onChange={handleChange}
            />
            {validator.current.message(
              "first name",
              formData.first_name,
              "required"
            )}
          </div>
        </div>
        <div className="col-md-6 mb-4">
          <div className="form-outline">
            <input
              type="text"
              id="lastName"
              name="last_name"
              placeholder="Last Name"
              value={formData.last_name}
              className="form-control form-control-lg text-sm"
              onChange={handleChange}
            />
            {validator.current.message(
              "Last Name",
              formData.last_name,
              "required"
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 mb-4">
          <div className="form-outline">
            <input
              type="text"
              id="username"
              name="username"
              placeholder="Username"
              value={formData.username}
              className="form-control form-control-lg text-sm"
              onChange={handleChange}
            />
            {validator.current.message(
              "Username",
              formData.username,
              [
                "required",
                {
                  regex: /^[a-z0-9_.]{4,}$/,
                },
              ],
              {
                messages: {
                  required: "The username is required.",
                  regex:
                    "The username must be lowercase alphanumeric and at least 4 characters long, and cannot contain spaces or special characters except underscore(_) and dot(.)",
                },
              }
            )}
            {formData.username && !isAvailable.username && (
              <span className="text-red text-sm">
                This username is already taken
              </span>
            )}
          </div>
        </div>
        <div className="col-md-6 mb-4">
          <div className="form-outline">
            <input
              type="text"
              id="phone"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              className="form-control form-control-lg text-sm"
              onChange={handleChange}
              required
            />
            {validator.current.message(
              "Phone",
              formData.phone,
              "required|phone"
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 pt-1">
          <div className="form-group mb-2">
            <select
              name="country_id"
              value={formData?.country_id}
              className="form-select form-select-sm"
              aria-label="Default select example"
              onChange={handleChange}
              style={{ height: "34px" }}
            >
              <option value="">
                Select country
              </option>
              {(countryData?.data ? countryData.data : []).map((country: { country_code: string, country_name: string }) => (
                <option key={country?.country_code} value={country?.country_code}>
                  {country?.country_name}
                </option>
              ))}
            </select>
            {validator.current.message(
              "Country",
              formData.country_id,
              "required"
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12  pt-3">
          <div className="form-group mb-2">
            <div className="mb-4">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-control form-control-lg text-sm"
                placeholder="Email"
                required
              />
              <span className="mb-5">
                {validator.current.message("email", formData.email, "required|email")}
              </span>
              {!isAvailable.email && (
                <span className="text-red text-sm">
                  This email is already taken
                </span>
              )}
            </div>
            <div className="mb-4">
              <div className="input-group ">
                <input
                  type={passwordShown ? "text" : "password"}
                  name="password"
                  onChange={handleChange}
                  value={formData.password}
                  className="form-control form-control-lg text-sm"
                  placeholder="Password"
                  aria-label="Sizing example input"
                  aria-describedby="inputGroup-sizing-default"
                  autoComplete="new-password"
                  // onCopy={(e) => e.preventDefault()}
                  // onPaste={(e) => e.preventDefault()}
                  required
                />
                {passwordShown ? (
                  <i className="input-group-text" onClick={togglePasswordVisiblity}>{eye}</i>
                ) : (
                  <i className="input-group-text" onClick={togglePasswordVisiblity}>{eyeSlash}</i>
                )}
              </div>
              {validator.current.message("password", formData.password, "required")}
            </div>

            <div className="mb-4">
              <div className="input-group">
                <input
                  type={confirmpasswordShown ? "text" : "password"}
                  name="ConfirmPassword"
                  onChange={handleChange}
                  value={formData.ConfirmPassword}
                  className="form-control form-control-lg text-sm"
                  placeholder="Confirm Password"
                  required
                // onCopy={(e) => e.preventDefault()}
                // onPaste={(e) => e.preventDefault()}
                />
                {confirmpasswordShown ? (
                  <i className="input-group-text" onClick={toggleConfirmPasswordVisiblity}>{eye}</i>
                ) : (
                  <i className="input-group-text" onClick={toggleConfirmPasswordVisiblity}>{eyeSlash}</i>
                )}
              </div>
              {validator.current.message(
                "confirmPassword",
                formData.ConfirmPassword,
                `required|in:${formData.password}`,
                {
                  messages: {
                    required: "Confirm password is required.",
                    in: "Passwords do not match.",
                  },
                }
              )}
              {
                <span className="text-red text-sm">
                  {isFormSubmit &&
                    formData.password.length &&
                    !password_check(formData.password)
                    ? "The password should be at least 8 characters and must have at least 1 lower case letter, 1 upper case letter, 1 number, and 1 special character (!, #, $, *)."
                    : ""}
                </span>
              }
            </div>
            <div className="mb-4">
              <label className="form-label px-1">
                <input
                  name="agreed"
                  type="checkbox"
                  className="form-check-input"
                  id="agreed"
                  checked={formData.agreed}
                  onChange={handleChange}
                />
                <span className="text-secondary text-sm">
                  {" "}
                  I agree with the{" "}
                  <Link href="/terms-and-condition">
                    Terms and Conditions
                  </Link>
                </span>
              </label>
              {validator.current.message("terms and conditions", formData.agreed, "required|accepted")}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        name="submit"
        className="btn bg-shibui-btn text-white w-100 block mt-3"
        onClick={handlesubmit}
        disabled={loading}
      // disabled={isRegisterLoader}
      >
        {loading ? "Registering..." : "Sign Up"}
      </button>

      <div className="mt-3 text-center d-flex align-items-center justify-content-evenly">
        <Link
          href="/login"
          onClick={() => makeNavigationSiteAnalytics("Login button", "/login")}
          className="text-center"
        >
          <FontAwesomeIcon icon={faArrowCircleLeft} /> Back to login
        </Link>
        <Link
          href="/screener"
          onClick={() => makeNavigationSiteAnalytics("go to screener button", "/screener")}
          className="text-center"
        >
          Go to screener <FontAwesomeIcon icon={faArrowCircleRight} />
        </Link>
      </div>
    </form>
  )
}

const mapStatesToProps = ({
  authenticate: { isRegisterLoader = false },
  common: { countryData, currencyData, industryData, regionData },
}: any) => {
  return { isRegisterLoader, countryData, currencyData, industryData, regionData };
};

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators(
    {
      ...authenticateAction,
      ...commonAction,
    },
    dispatch
  );
};

export default memo(connect(mapStatesToProps, mapDispatchToProps)(RegisterForm));